import { types, Instance, flow } from 'mobx-state-tree';
import api from '../services/api';

// User profile model
const UserProfile = types.model('UserProfile', {
  id: types.string,
  email: types.string,
  name: types.maybeNull(types.string),
  firstName: types.maybeNull(types.string),
  lastName: types.maybeNull(types.string),
  initials: types.optional(types.string, ''),
  avatar: types.maybeNull(types.string),
  avatarUrl: types.maybeNull(types.string),
  organizationId: types.maybeNull(types.string),
  role: types.optional(types.string, 'member'),
  status: types.optional(types.enumeration(['active', 'pending', 'invited', 'inactive']), 'active'),
  isActive: types.optional(types.boolean, true),
  lastSeen: types.maybeNull(types.number),
})
.views(self => ({
  get displayName() {
    if (self.name) return self.name;
    if (self.firstName || self.lastName) {
      return `${self.firstName || ''} ${self.lastName || ''}`.trim();
    }
    return self.email;
  },
  get displayInitials() {
    if (self.initials) return self.initials;
    if (self.firstName || self.lastName) {
      return `${self.firstName?.charAt(0) || ''}${self.lastName?.charAt(0) || ''}`.toUpperCase();
    }
    if (self.name) {
      const parts = self.name.split(' ');
      return parts.map(p => p.charAt(0)).slice(0, 2).join('').toUpperCase();
    }
    return self.email.charAt(0).toUpperCase();
  },
  get avatarSource() {
    return self.avatarUrl || self.avatar;
  }
}));

// Organization model
const Organization = types.model('Organization', {
  id: types.string,
  slug: types.string,
  name: types.string,
  description: types.maybeNull(types.string),
  status: types.optional(types.enumeration(['active', 'inactive', 'suspended']), 'active'),
  ownerId: types.string,
  settings: types.maybeNull(types.frozen()),
  createdAt: types.maybeNull(types.string),
});

// User store to manage user data and profiles
const UserStore = types
  .model('UserStore', {
    users: types.array(UserProfile),
    organizations: types.array(Organization),
    currentOrganizationId: types.maybeNull(types.string),
    isLoading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
  })
  .actions(self => ({
    setLoading(loading: boolean) {
      self.isLoading = loading;
    },
    
    setError(error: string | null) {
      self.error = error;
    },
    
    // Load organization data
    loadOrganization: flow(function* (orgSlug: string) {
      const callId = Math.random().toString(36).substr(2, 9);
      console.log(`🏢 [UserStore.loadOrganization] CALL START #${callId} with slug:`, orgSlug);
      
      if (self.isLoading) {
        console.log(`❌ [UserStore.loadOrganization] #${callId} Already loading, skipping duplicate call`);
        return;
      }
      
      self.isLoading = true;
      try {
        console.log(`🌐 [UserStore.loadOrganization] #${callId} Fetching organization data...`);
        
        // Use authenticated axios instance to include Stytch JWT
        const response = yield api.get(`/organizations/slug/${encodeURIComponent(orgSlug)}`);
        
        self.hydrateOrganization(response.data);
        console.log(`✅ [UserStore.loadOrganization] #${callId} Organization loaded:`, response.data.name);
      } catch (error) {
        console.error(`❌ [UserStore.loadOrganization] #${callId} Organization load failed:`, error);
        self.error = `Failed to load organization: ${error.message}`;
        // Don't retry if it's an auth error (401)
        if (error.response?.status === 401) {
          console.log(`🔐 [UserStore.loadOrganization] #${callId} Auth error detected, not retrying`);
          // Don't clear auth here to avoid circular dependency
          // The auth failure will be handled by AuthStore itself
        }
      } finally {
        console.log(`🏁 [UserStore.loadOrganization] #${callId} COMPLETE - isLoading set to false`);
        self.isLoading = false;
      }
    }),

    clearOrganizations() {
      console.log('🧹 [UserStore.clearOrganizations] Clearing all organization data');
      self.currentOrganizationId = undefined;
      self.organizations.clear();
    },

    hydrateOrganization(orgData: any) {
      if (!orgData?.id) {
        console.warn('[UserStore.hydrateOrganization] Invalid org data provided');
        return;
      }

      console.log(`🏢 [UserStore.hydrateOrganization] Hydrating org:`, { id: orgData.id, slug: orgData.slug, name: orgData.name });

      // First, ensure we have all required fields for the Organization model
      const completeOrgData = {
        id: orgData.id,
        slug: orgData.slug || 'unknown-slug',
        name: orgData.name || 'Unknown Organization',
        description: orgData.description || undefined,
        status: orgData.status || 'active',
        ownerId: orgData.ownerId || 'unknown-owner',
        settings: orgData.settings || undefined,
        createdAt: orgData.createdAt || new Date().toISOString(),
      };

      // Clear any existing reference first to prevent conflicts
      self.currentOrganizationId = undefined;

      // Find existing or add new
      const existing = self.organizations.find(org => org.id === orgData.id);
      if (existing) {
        console.log(`🔄 [UserStore.hydrateOrganization] Updating existing org:`, existing.slug);
        Object.assign(existing, completeOrgData);
      } else {
        console.log(`➕ [UserStore.hydrateOrganization] Adding new org to array:`, completeOrgData.slug);
        self.organizations.push(completeOrgData);
      }

      // Set ID reference after ensuring org exists in array
      self.currentOrganizationId = completeOrgData.id;
      console.log(`🎯 [UserStore.hydrateOrganization] Current org ID set to:`, self.currentOrganizationId);
    },
    
    // Load users in current organization
    loadOrgUsers: flow(function* () {
      if (!self.currentOrganizationId) {
        console.warn('⚠️ [UserStore.loadOrgUsers] No currentOrganizationId, skipping');
        return;
      }
      
      console.log('🔄 [UserStore.loadOrgUsers] STARTING - Org ID:', self.currentOrganizationId);
      
      self.isLoading = true;
      try {
        console.log('📡 [UserStore] Fetching organization members via api service (with JWT)');
        
        // Use api service which includes JWT interceptor
        const response = yield api.get(`/organizations/${self.currentOrganizationId}/members`);
        
        const members = response.data.members || [];
        
        self.users.clear();
        members.forEach((member: any) => {
          // Skip soft-deleted members from the user store
          // (Admins still see them in Team Admin panel, but they don't appear in dropdowns)
          if (member.isDeleted) {
            console.log(`⏭️ Skipping deleted member: ${member.email}`);
            return;
          }
          
          self.users.push({
            id: member.id,
            email: member.email,
            name: member.name,
            firstName: member.firstName || '',
            lastName: member.lastName || '',
            initials: member.initials || '',
            avatar: member.avatarUrl,
            avatarUrl: member.avatarUrl,
            organizationId: self.currentOrganizationId,
            role: member.role || 'member',
            status: member.status || 'active',
            isActive: member.status === 'active',
            lastSeen: undefined,
          });
        });
        console.log(`✅ [UserStore] Loaded ${members.length} organization members`);
      } catch (error: any) {
        console.error('❌ [UserStore] Failed to load org members:', error);
        console.error('   Error details:', error.message);
        if (error.response) {
          console.error('   Response status:', error.response.status);
          console.error('   Response data:', error.response.data);
        }
        self.error = 'Failed to load organization members';
      } finally {
        self.isLoading = false;
      }
    }),
    
    addUser(userData: any) {
      const existing = self.users.find(u => u.id === userData.id);
      if (existing) {
        Object.assign(existing, userData);
      } else {
        self.users.push(userData);
      }
    },
    
    removeUser(userId: string) {
      const index = self.users.findIndex(u => u.id === userId);
      if (index >= 0) {
        self.users.splice(index, 1);
      }
    },
    
    updateUserAvatar(userId: string, avatarUrl: string) {
      const user = self.users.find(u => u.id === userId);
      if (user) {
        user.avatar = avatarUrl;
        user.avatarUrl = avatarUrl;
      }
    },
  }))
  .views(self => ({
    get activeUsers() {
      const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
      return self.users.filter(user => 
        user.isActive && (!user.lastSeen || user.lastSeen > fiveMinutesAgo)
      );
    },
    
    get currentOrganization() {
      if (!self.currentOrganizationId) return undefined;
      return self.organizations.find(org => org.id === self.currentOrganizationId);
    },
    
    get currentOrgData() {
      return self.currentOrganization;
    },
    
    getUserById(id: string) {
      // Try PostgreSQL ID first
      let user = self.users.find(u => u.id === id);
      // If not found, try Stytch member ID
      if (!user) {
        user = self.users.find(u => u.stytchMemberId === id);
      }
      return user;
    },
  }));

export type IUserStore = Instance<typeof UserStore>;
export { UserStore };